import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import SubmitFormRequestDialog from '../../components/SubmitFormRequestDialog';
import {
  Briefcase, FileText, ClipboardList, Plus, RefreshCw, Loader2,
  BookOpen, ShieldCheck, MessageSquare, Lightbulb, ArrowRight, Send,
  CheckCircle2, AlertCircle, Clock, Building2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PRIORITY_LABEL = { 1: 'P1 — Critical', 2: 'P2 — High', 3: 'P3 — Moderate', 4: 'P4 — Low' };

/**
 * Dashboard for `service_provider` role.
 *
 * Surfaces:
 *  - "Submit a request" wizard tied to org-configured request types,
 *    with optional linked Form (Forms engine) embedded inline.
 *  - "My service requests" queue.
 *  - Compliance hub + KB shortcuts + "updates feed" for status changes.
 */
export default function ServiceProviderDashboard() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, reqRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/service-request-types`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/service-requests?scope=mine&limit=50`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/service-requests/stats/me`, { headers: authHeader() }),
      ]);
      setTypes(typesRes.data?.types || []);
      setRequests(reqRes.data?.requests || []);
      setStats(statsRes.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-12 text-center text-[#64748B]" data-testid="sp-loading">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading provider portal…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-provider-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[#64748B] mt-1">Service Provider portal — deliver, comply, communicate.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="sp-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setSubmitOpen(true)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="submit-request-btn">
            <Plus className="w-4 h-4 mr-1" /> Submit a request
          </Button>
        </div>
      </div>

      {/* KPI strip ----------------------------------------------------- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={<Clock className="w-5 h-5 text-sky-600" />} label="Open" value={stats?.open ?? 0} testId="kpi-open" />
        <Kpi icon={<AlertCircle className="w-5 h-5 text-amber-600" />} label="In progress" value={stats?.in_progress ?? 0} testId="kpi-in-progress" />
        <Kpi icon={<AlertCircle className="w-5 h-5 text-rose-600" />} label="Blocked" value={stats?.blocked ?? 0} testId="kpi-blocked" />
        <Kpi icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />} label="Completed" value={stats?.completed ?? 0} testId="kpi-completed" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* My service requests (col-span 2) -------------------------- */}
        <Card className="border-[#E2E8F0] lg:col-span-2" data-testid="my-requests-card">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base text-[#0A2342] flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-[#0E7490]" /> My service requests
            </CardTitle>
            <span className="text-xs text-[#94A3B8]">{requests.length} total</span>
          </CardHeader>
          <CardContent className="p-0">
            {requests.length === 0 ? (
              <div className="text-center py-12 text-sm text-[#94A3B8]" data-testid="no-requests">
                <Briefcase className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                You haven&apos;t submitted any requests yet.
                <div className="mt-3">
                  <Button size="sm" onClick={() => setSubmitOpen(true)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="submit-first-btn">
                    <Plus className="w-4 h-4 mr-1" /> Submit your first request
                  </Button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[#64748B] border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <th className="px-3 py-2 font-medium">Title</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Priority</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(r => (
                      <tr key={r.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`my-request-row-${r.id}`}>
                        <td className="px-3 py-2 text-[#0A2342] font-medium">{r.title}</td>
                        <td className="px-3 py-2 text-[#64748B]">{r.type_name || '—'}</td>
                        <td className="px-3 py-2 text-[#64748B]">{PRIORITY_LABEL[r.priority] || r.priority}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={statusBadge(r.status)}>{(r.status || '').replace('_', ' ')}</Badge>
                        </td>
                        <td className="px-3 py-2 text-[#64748B] whitespace-nowrap">{(r.created_at || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Side column ------------------------------------------------ */}
        <div className="space-y-6">
          {/* Compliance & Reporting */}
          <Card className="border-[#E2E8F0]" data-testid="compliance-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#0A2342] flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Compliance &amp; Reporting
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <SideLink to="/knowledge" icon={<BookOpen className="w-3.5 h-3.5" />} label="HIPAA & policies" />
              <SideLink to="/inservice" icon={<FileText className="w-3.5 h-3.5" />} label="My in-service training" />
              <SideLink to="/forms" icon={<FileText className="w-3.5 h-3.5" />} label="Reports & forms" />
            </CardContent>
          </Card>

          {/* Customer Support & Communication */}
          <Card className="border-[#E2E8F0]" data-testid="support-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#0A2342] flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-sky-600" /> Customer Support
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <SideLink to="/support" icon={<MessageSquare className="w-3.5 h-3.5" />} label="Open a support ticket" />
              <SideLink to="/settings/profile" icon={<Building2 className="w-3.5 h-3.5" />} label="Update profile" />
            </CardContent>
          </Card>

          {/* Problem-Solving & Optimization */}
          <Card className="border-[#E2E8F0]" data-testid="optimization-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#0A2342] flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" /> Problem solving
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <SideLink to="/knowledge" icon={<BookOpen className="w-3.5 h-3.5" />} label="Knowledge base articles" />
              <SideLink to="/forum" icon={<MessageSquare className="w-3.5 h-3.5" />} label="Community forum" />
            </CardContent>
          </Card>
        </div>
      </div>

      <SubmitFormRequestDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmitted={() => { setSubmitOpen(false); load(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------
// Legacy submit dialog kept for fallback when admin-configured request
// types are used (rare). Now superseded by SubmitFormRequestDialog.
// ---------------------------------------------------------------------
// eslint-disable-next-line no-unused-vars
function LegacySubmitRequestDialog({ open, onClose, types, onSubmitted }) {
  const [typeId, setTypeId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(3);
  const [busy, setBusy] = useState(false);
  const [linkedForm, setLinkedForm] = useState(null);
  const [formData, setFormData] = useState({});

  const type = types.find(t => t.id === typeId);

  useEffect(() => {
    setLinkedForm(null);
    setFormData({});
    if (type?.linked_form_id) {
      (async () => {
        try {
          const r = await axios.get(`${API_URL}/api/forms/${type.linked_form_id}`, { headers: authHeader() });
          setLinkedForm(r.data);
        } catch { /* ignore */ }
      })();
    }
  }, [typeId, type]);

  const submit = async () => {
    if (!typeId || !title.trim()) { toast.error('Pick a type and add a title'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/service-requests`,
        { type_id: typeId, title, description, priority, form_data: linkedForm ? formData : null },
        { headers: authHeader() },
      );
      toast.success('Request submitted');
      setTitle(''); setDescription(''); setTypeId(''); setFormData({}); setLinkedForm(null);
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="submit-dialog">
        <DialogHeader><DialogTitle>Submit a service request</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <Label>Request type *</Label>
            <select
              value={typeId} onChange={(e) => setTypeId(e.target.value)}
              className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-2 bg-white"
              data-testid="type-select"
            >
              <option value="">Pick a request type…</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.linked_form_name ? ` · Form: ${t.linked_form_name}` : ''}
                </option>
              ))}
            </select>
            {types.length === 0 && (
              <p className="text-[10px] text-amber-700 mt-1">
                No request types configured yet. Ask your administrator to create one at <Link to="/admin/service-request-types" className="underline">Service Request Types</Link>.
              </p>
            )}
            {type?.description && <p className="text-xs text-[#64748B] mt-1">{type.description}</p>}
          </div>
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="One-line summary" data-testid="title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm"
              placeholder="More context, scope, urgency…"
              data-testid="description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <select
                value={priority} onChange={(e) => setPriority(parseInt(e.target.value, 10))}
                className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-2 bg-white"
                data-testid="priority-select"
              >
                {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Inline form fields when type has a linked form */}
          {linkedForm && (
            <Card className="border-[#0E7490]/30 bg-[#0E7490]/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-[#0E7490] flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" /> Linked form: {linkedForm.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(linkedForm.fields || []).slice(0, 12).map((f, i) => (
                  <SimpleFieldRenderer
                    key={f.id || f.key || i}
                    field={f}
                    value={formData[f.id || f.key || `f${i}`] || ''}
                    onChange={(v) => setFormData(prev => ({ ...prev, [f.id || f.key || `f${i}`]: v }))}
                  />
                ))}
                {(linkedForm.fields || []).length > 12 && (
                  <p className="text-[10px] text-[#94A3B8]">+ {linkedForm.fields.length - 12} more fields will be captured on the full form on submit.</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !typeId || !title.trim()} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="confirm-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SimpleFieldRenderer({ field, value, onChange }) {
  const lbl = field.label || field.name || field.id;
  const type = (field.type || 'text').toLowerCase();
  if (type === 'textarea') {
    return (
      <div>
        <Label className="text-xs">{lbl}{field.required ? ' *' : ''}</Label>
        <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm bg-white" />
      </div>
    );
  }
  if (type === 'select' || type === 'dropdown') {
    return (
      <div>
        <Label className="text-xs">{lbl}{field.required ? ' *' : ''}</Label>
        <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm">
          <option value="">—</option>
          {(field.options || []).map((o, i) => (
            <option key={i} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label || o.value}</option>
          ))}
        </select>
      </div>
    );
  }
  if (type === 'checkbox' || type === 'bool' || type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-xs text-[#0A2342]">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {lbl}
      </label>
    );
  }
  return (
    <div>
      <Label className="text-xs">{lbl}{field.required ? ' *' : ''}</Label>
      <Input type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'} value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm bg-white" />
    </div>
  );
}

function Kpi({ icon, label, value, testId }) {
  return (
    <Card className="border-[#E2E8F0]" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-[#64748B]">{icon} {label}</div>
        <div className="text-2xl font-bold text-[#0A2342] mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
function SideLink({ to, icon, label }) {
  return (
    <Link to={to} className="flex items-center justify-between text-[#0A2342] hover:bg-[#F8FAFC] rounded px-2 py-1.5 -mx-1">
      <span className="flex items-center gap-2"><span className="text-[#0E7490]">{icon}</span> {label}</span>
      <ArrowRight className="w-3 h-3 text-[#94A3B8]" />
    </Link>
  );
}
function statusBadge(status) {
  switch ((status || '').toLowerCase()) {
    case 'open': return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'in_progress': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'blocked': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'cancelled': return 'bg-slate-50 text-slate-600 border-slate-200';
    default: return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}
